/*
 * Wording and arithmetic for showing plans. No prices live here: every price,
 * visit count and minimum term comes from /subscription-plans, which reads the
 * same rows checkout charges from (see CLAUDE.md). What is here is what the
 * server has no column for: names, and policy wording that is the same for
 * every plan.
 */

export const PLAN_NAMES = {
  STANDARD: "Youro One",
  PREMIUM: "Youro Plus",
  ONETIME: "Youro On Demand",
};

export const PRICING_COPY = {
  // How soon a provider answers a message.
  messageResponseBusinessDays: "1–2",
  // How long after an On Demand purchase it still counts toward a membership.
  // The server enforces it (OnDemandCredit.WINDOW_DAYS); this is the wording.
  onDemandCreditWindowDays: 30,
};

/** The /subscription-plans query that includes monthly plans. */
export const PLANS_PATH = "/subscription-plans?includeMonthly=true";

export const isMonthly = (plan) => plan?.billingInterval === "MONTH";

/** "/month", "/year", or "one time" for a pass. */
export const periodLabel = (plan) => {
  if (!plan?.recurring) return "one time";
  return isMonthly(plan) ? "/month" : "/year";
};

/** The yearly and monthly versions of one tier (STANDARD or PREMIUM). Either may be missing. */
export const tierPlans = (plans, tier) => ({
  annual: plans.find((p) => p.name === tier && p.recurring && !isMonthly(p)) || null,
  monthly: plans.find((p) => p.name === tier && isMonthly(p)) || null,
});

/** Whole percent saved by paying yearly, rounded down; null if either price is missing. */
export const savePercent = (annual, monthly) => {
  if (!annual?.price || !monthly?.price) return null;
  const saved = Math.floor((1 - annual.price / (monthly.price * 12)) * 100);
  return saved > 0 ? saved : null;
};

/** The largest yearly saving across the membership tiers, or null. */
export const maxSavePercent = (plans) => {
  const all = ["STANDARD", "PREMIUM"]
    .map((tier) => {
      const { annual, monthly } = tierPlans(plans, tier);
      return savePercent(annual, monthly);
    })
    .filter((n) => n != null);
  return all.length ? Math.max(...all) : null;
};

/** A yearly price as a monthly figure, to the nearest dollar ($599 → $50). */
export const monthlyEquivalent = (annual) => Math.round(annual.price / 12);

/** "3-month minimum, then cancel anytime", or null when the plan has no minimum. */
export const minimumTermLine = (plan) =>
  plan?.minimumTermMonths ? `${plan.minimumTermMonths}-month minimum, then cancel anytime` : null;

/**
 * Whether a change from one membership to another is charged and starts now
 * (same billing period, higher tier) rather than waiting for the renewal date.
 * The server decides (MembershipRules.changeTakesEffectNow); this only picks
 * the wording, and mirrors it.
 */
export const changeTakesEffectNow = (current, target) => {
  const rank = { STANDARD: 1, PREMIUM: 2 };
  return isMonthly(current) === isMonthly(target) && (rank[target.name] || 0) > (rank[current.name] || 0);
};

/**
 * The day access ends if a membership is cancelled now: the end of the paid
 * period, or of the minimum term if that is later. ISO dates compare as strings.
 */
export const accessEndsOnCancel = (subscription) => {
  const end = subscription?.endDate || null;
  const minimum = subscription?.minimumTermEnds || null;
  if (minimum && (!end || minimum > end)) return minimum;
  return end;
};
